import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { FASTAPI_HOST } from '../settings/config';
import { DEFAULT_BUDDY, REACTION_KEYS, REACTION_PACKS, apiBuddyToProfile, draftFromBuddy, loadBuddies, newBuddyDraft } from '../settings/buddyProfiles';
import { getEnabledBuddyVoices } from '../settings/buddyVoiceCatalog';
import { deleteLocalBuddyAvatar } from '../settings/buddyAvatarStore';
import { getBuddyProfiles, setCachedBuddyProfiles } from '../settings/buddySessionCache';
import { showStyledConfirm } from './StyledAlert';

export default function BuddyManagerDialog({ open, onClose, buddies: controlledBuddies, setBuddies: setControlledBuddies, buddyId: controlledBuddyId, voice, speed, onBuddySelected, onVoiceSettingsChanged, currentLang = 'English', t = {} }) {
  const [localBuddies, setLocalBuddies] = useState(loadBuddies);
  const buddies = controlledBuddies || localBuddies;
  const setBuddies = setControlledBuddies || setLocalBuddies;
  const activeId = controlledBuddyId || window.localStorage.getItem('freeTalkBuddyId') || 'leafy';
  const [editingId, setEditingId] = useState(activeId);
  const [draft, setDraft] = useState(() => draftFromBuddy(buddies.find((item) => item.id === activeId) || DEFAULT_BUDDY));
  const [saving, setSaving] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const [error, setError] = useState('');
  const previewAudioRef = useRef(null);
  const previewUrlRef = useRef('');
  const editingBuddy = buddies.find((item) => item.id === editingId);
  const authConfig = () => ({ headers: { Authorization: `Bearer ${Cookies.get('user_token') || ''}` } });
  const tr = (key, fallback, values = {}) => Object.entries(values).reduce(
    (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
    String(t[key] || fallback),
  );
  const voiceOptions = getEnabledBuddyVoices();
  const voiceDescription = (option) => t[option.descriptionKey] || option.description;

  const stopVoicePreview = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setPreviewingVoice(false);
  };

  useEffect(() => () => stopVoicePreview(), []);
  useEffect(() => {
    if (!open) stopVoicePreview();
  }, [open]);

  const previewVoice = async () => {
    if (previewingVoice) {
      stopVoicePreview();
      return;
    }
    stopVoicePreview();
    setPreviewingVoice(true);
    setError('');
    const buddyName = draft.name.trim() || tr('buddy_default_name', 'your buddy');
    const sample = String(t.photo_story_voice_preview_sample || `Hello! I am ${buddyName}. This is how my voice sounds.`)
      .replace('{name}', '')
      .replaceAll('Leafy', buddyName);
    try {
      const response = await axios.post(`${FASTAPI_HOST}/api/photo-story/tts`, {
        text: sample,
        voice: draft.voice,
        language: currentLang || 'English',
      }, { ...authConfig(), responseType: 'arraybuffer' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }));
      const audio = new Audio(url);
      previewUrlRef.current = url;
      previewAudioRef.current = audio;
      audio.playbackRate = Number(draft.speed) || 1;
      audio.preservesPitch = true;
      audio.onended = stopVoicePreview;
      audio.onerror = () => {
        stopVoicePreview();
        setError(tr('photo_story_voice_preview_error', 'The voice preview is unavailable right now.'));
      };
      await audio.play();
    } catch (requestError) {
      stopVoicePreview();
      setError(requestError?.response?.data?.detail || tr('photo_story_voice_preview_error', 'The voice preview is unavailable right now.'));
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError('');
    getBuddyProfiles().then((loaded) => {
      if (cancelled) return;
      if (!loaded.length) return;
      setBuddies(loaded);
      const selected = loaded.find((item) => item.id === activeId) || loaded.find((item) => item.isDefault) || loaded[0];
      setEditingId(selected.id);
      setDraft({ ...draftFromBuddy(selected), voice: voice || selected.voice, speed: Number(speed) || selected.speed });
    }).catch((requestError) => {
      if (!cancelled) setError(requestError?.response?.data?.detail || tr('buddy_load_error', 'Buddy profiles could not be loaded.'));
    });
    return () => { cancelled = true; };
  }, [open]);

  const chooseBuddy = (id) => {
    stopVoicePreview();
    if (!id) { setEditingId(''); setDraft(newBuddyDraft()); return; }
    const selected = buddies.find((item) => item.id === id) || DEFAULT_BUDDY;
    setEditingId(selected.id);
    setDraft(draftFromBuddy(selected));
  };

  const loadImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type) || file.size > 750 * 1024) {
      setError(tr('buddy_image_error', 'Please choose a JPG, PNG, WebP, or GIF image smaller than 750 KB.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDraft((current) => ({ ...current, image: String(reader.result || '') }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    const name = draft.name.trim().slice(0, 40);
    if (!name) return;
    const savedBuddy = {
      id: editingId || '', name, image: draft.image || '',
      reactionPackId: draft.reactionPackId,
      reactions: Object.fromEntries(REACTION_KEYS.map((key) => [key, String(draft.reactions[key] || REACTION_PACKS.simple.reactions[key]).trim().slice(0, 16)])),
      voice: draft.voice, speed: Number(draft.speed) || 1,
      personality: draft.personality.trim().slice(0, 240), presentation: draft.presentation || 'static',
      isBuiltin: buddies.find((item) => item.id === editingId)?.isBuiltin || false,
      isDefault: true,
    };
    setSaving(true); setError('');
    try {
      const payload = {
        name: savedBuddy.name, schema_version: 1,
        persona_config: { personality: savedBuddy.personality },
        voice_config: { provider: 'openai', voice: savedBuddy.voice },
        appearance_config: { mode: savedBuddy.presentation, fallbackAsset: savedBuddy.isBuiltin ? '/leafy_icon.jpeg' : '', avatarAssetId: null },
        reaction_config: { packId: savedBuddy.reactionPackId, reactions: savedBuddy.reactions },
        behavior_config: {},
        capabilities: { voice: true, customLocalAvatar: true, cloudAssets: true, animated2d: false, model3d: false, liveRealtime: false },
        is_default: true,
      };
      const response = editingId
        ? await axios.put(`${FASTAPI_HOST}/api/user-buddies/${editingId}`, payload, authConfig())
        : await axios.post(`${FASTAPI_HOST}/api/user-buddies`, payload, authConfig());
      await axios.put(`${FASTAPI_HOST}/api/user-preferences`, {
        photo_leafy_speech_speed: savedBuddy.speed,
      }, authConfig());
      let persisted = apiBuddyToProfile(response.data);
      persisted.speed = savedBuddy.speed;
      if (savedBuddy.image?.startsWith('data:')) {
        const imageBlob = await (await fetch(savedBuddy.image)).blob();
        const imageForm = new FormData();
        imageForm.append('image', imageBlob, `buddy-avatar.${imageBlob.type.split('/')[1] || 'png'}`);
        const avatarResponse = await axios.put(
          `${FASTAPI_HOST}/api/user-buddies/${persisted.id}/avatar`,
          imageForm,
          { ...authConfig(), headers: { ...authConfig().headers, 'Content-Type': 'multipart/form-data' } },
        );
        persisted = { ...apiBuddyToProfile(avatarResponse.data), speed: savedBuddy.speed };
        await deleteLocalBuddyAvatar(persisted.id);
      } else if (!savedBuddy.image && editingId) {
        await axios.delete(`${FASTAPI_HOST}/api/user-buddies/${persisted.id}/avatar`, authConfig());
        await deleteLocalBuddyAvatar(persisted.id);
      }
      const next = editingId ? buddies.map((item) => item.id === editingId ? persisted : { ...item, isDefault: false }) : [...buddies.map((item) => ({ ...item, isDefault: false })), persisted];
      setBuddies(next);
      setCachedBuddyProfiles(next);
      window.localStorage.setItem('freeTalkBuddyId', persisted.id);
      onBuddySelected?.(persisted);
      onVoiceSettingsChanged?.({ voice: persisted.voice, speed: persisted.speed });
      window.dispatchEvent(new CustomEvent('buddy-settings-updated', { detail: persisted }));
      onClose();
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || requestError?.message || tr('buddy_save_error', 'Buddy settings could not be saved.'));
    } finally { setSaving(false); }
  };

  const resetLeafy = async (buddyToRestore) => {
    if (!buddyToRestore?.isBuiltin) return;
    const confirmed = await showStyledConfirm(
      tr('buddy_restore_confirm', 'Restore {name} to its original settings? Custom settings and the local image will be removed.', { name: buddyToRestore.name }),
      { confirmLabel: tr('buddy_restore', 'Restore'), cancelLabel: tr('sb_cancel', 'Cancel') },
    );
    if (!confirmed) return;
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${FASTAPI_HOST}/api/user-buddies/${buddyToRestore.id}/reset`, {}, authConfig());
      const restored = apiBuddyToProfile(data);
      restored.speed = Number(draft.speed) || Number(speed) || 1;
      await deleteLocalBuddyAvatar(buddyToRestore.id);
      const next = buddies.map((item) => item.id === buddyToRestore.id ? restored : item);
      setBuddies(next);
      setCachedBuddyProfiles(next);
      if (editingId === buddyToRestore.id) setDraft(draftFromBuddy(restored));
      if (activeId === buddyToRestore.id) {
        onBuddySelected?.(restored);
        onVoiceSettingsChanged?.({ voice: restored.voice, speed: restored.speed });
        window.dispatchEvent(new CustomEvent('buddy-settings-updated', { detail: restored }));
      }
    } catch (requestError) { setError(requestError?.response?.data?.detail || tr('buddy_restore_error', 'Leafy could not be restored.')); }
    finally { setSaving(false); }
  };

  const deleteBuddy = async (buddyToDelete) => {
    if (!buddyToDelete || buddyToDelete.isBuiltin) return;
    const confirmed = await showStyledConfirm(
      tr('buddy_delete_confirm', 'Delete the buddy "{name}"? This cannot be undone.', { name: buddyToDelete.name }),
      { confirmLabel: tr('buddy_delete', 'Delete buddy'), cancelLabel: tr('sb_cancel', 'Cancel') },
    );
    if (!confirmed) return;
    setSaving(true); setError('');
    try {
      await axios.delete(`${FASTAPI_HOST}/api/user-buddies/${buddyToDelete.id}`, authConfig());
      await deleteLocalBuddyAvatar(buddyToDelete.id);
      const next = buddies.filter((item) => item.id !== buddyToDelete.id);
      setBuddies(next);
      setCachedBuddyProfiles(next);
      if (editingId === buddyToDelete.id || activeId === buddyToDelete.id) {
        const fallbackProfile = next.find((item) => item.isDefault) || next[0] || DEFAULT_BUDDY;
        const fallback = { ...fallbackProfile, speed: Number(draft.speed) || Number(speed) || 1 };
        if (editingId === buddyToDelete.id) {
          setEditingId(fallback.id);
          setDraft(draftFromBuddy(fallback));
        }
        if (activeId === buddyToDelete.id) {
          window.localStorage.setItem('freeTalkBuddyId', fallback.id);
          onBuddySelected?.(fallback);
          window.dispatchEvent(new CustomEvent('buddy-settings-updated', { detail: fallback }));
        }
      }
    } catch (requestError) { setError(requestError?.response?.data?.detail || tr('buddy_delete_error', 'Buddy could not be deleted.')); }
    finally { setSaving(false); }
  };

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, maxHeight: '88vh', bgcolor: '#FDFEFC' } }}>
    <DialogTitle sx={{ pb: 1, fontWeight: 800, color: '#2D3913', borderBottom: '1px solid #C1C7B8' }}>{tr('buddy_manage_title', 'Manage Buddies')}</DialogTitle>
    <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}><Stack spacing={1.5} sx={{ pt: 1 }}>
      {error && <Alert severity="error">{typeof error === 'string' ? error : tr('buddy_save_error', 'Buddy settings could not be saved.')}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
        <FormControl size="small">
          <InputLabel id="buddy-select-label">{tr('buddy_select', 'Buddy to manage')}</InputLabel>
          <Select
            id="buddy-select"
            labelId="buddy-select-label"
            value={editingId || '__new_buddy__'}
            label={tr('buddy_select', 'Buddy to manage')}
            disabled={saving}
            renderValue={(value) => value === '__new_buddy__' ? tr('buddy_new', 'New buddy') : buddies.find((item) => item.id === value)?.name || ''}
            onChange={(event) => event.target.value === '__add_buddy__' ? chooseBuddy('') : chooseBuddy(event.target.value)}
          >
            {!editingId && <MenuItem value="__new_buddy__" disabled>{tr('buddy_new', 'New buddy')}</MenuItem>}
            {buddies.map((item) => (
              <MenuItem key={item.id} value={item.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography component="span" noWrap sx={{ flex: 1 }}>{item.name}</Typography>
                <Tooltip title={item.isBuiltin ? tr('buddy_restore_named', 'Restore {name}', { name: item.name }) : tr('buddy_delete_named', 'Delete {name}', { name: item.name })} placement="left">
                  <IconButton
                    size="small"
                    color={item.isBuiltin ? 'inherit' : 'error'}
                    aria-label={item.isBuiltin ? tr('buddy_restore_named', 'Restore {name}', { name: item.name }) : tr('buddy_delete_named', 'Delete {name}', { name: item.name })}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (item.isBuiltin) resetLeafy(item);
                      else deleteBuddy(item);
                    }}
                  >
                    {item.isBuiltin ? <RestoreRoundedIcon fontSize="small" /> : <DeleteOutlineRoundedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </MenuItem>
            ))}
            <MenuItem value="__add_buddy__" sx={{ color: '#586F3C', fontWeight: 700 }}>＋ {tr('buddy_add', 'Add buddy')}…</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" label={tr('buddy_name', 'Buddy name')} value={draft.name} disabled={Boolean(editingBuddy?.isBuiltin)} inputProps={{ maxLength: 40 }} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
      </Box>
      {editingBuddy?.isBuiltin && <Typography variant="caption" color="text.secondary">{tr('buddy_leafy_protected', 'Leafy is protected. Only its speech speed can be customized.')}</Typography>}
      <TextField size="small" label={tr('buddy_personality', 'Personality')} value={draft.personality} disabled={Boolean(editingBuddy?.isBuiltin)} multiline minRows={2} inputProps={{ maxLength: 240 }} onChange={(event) => setDraft({ ...draft, personality: event.target.value })} />

      <Box sx={{ p: 1.5, border: '1px solid #C1C7B8', borderRadius: 2.5, bgcolor: '#F5F8F1' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800 }}>{tr('buddy_voice_reactions', 'Voice and reactions')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mb: 1.25 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <FormControl size="small" disabled={Boolean(editingBuddy?.isBuiltin)} sx={{ flex: 1, minWidth: 0 }}><InputLabel id="buddy-voice-label">{tr('buddy_voice', 'Voice')}</InputLabel><Select id="buddy-voice" labelId="buddy-voice-label" value={draft.voice} label={tr('buddy_voice', 'Voice')} onChange={(event) => setDraft({ ...draft, voice: event.target.value })}>{voiceOptions.map((option) => <MenuItem key={option.id} value={option.id}>{voiceDescription(option)}</MenuItem>)}</Select></FormControl>
            <Tooltip title={t.photo_story_preview_voice || 'Preview'}>
              <span>
                <IconButton size="small" onClick={previewVoice} disabled={saving} aria-label={t.photo_story_preview_voice || 'Preview voice'} sx={{ border: '1px solid #AFC48A', color: '#586F3C' }}>
                  {previewingVoice ? <CircularProgress size={18} color="inherit" /> : <VolumeUpRoundedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <FormControl size="small" disabled={Boolean(editingBuddy?.isBuiltin)}><InputLabel id="buddy-reaction-pack-label">{tr('buddy_reaction_pack', 'Reaction pack')}</InputLabel><Select id="buddy-reaction-pack" labelId="buddy-reaction-pack-label" value={draft.reactionPackId} label={tr('buddy_reaction_pack', 'Reaction pack')} onChange={(event) => { const reactionPackId = event.target.value; setDraft({ ...draft, reactionPackId, reactions: { ...REACTION_PACKS[reactionPackId].reactions } }); }}>{Object.entries(REACTION_PACKS).map(([id, pack]) => <MenuItem key={id} value={id}>{tr(`buddy_reaction_pack_${id}`, pack.label)}</MenuItem>)}{draft.reactionPackId === 'custom' && <MenuItem value="custom" disabled>{tr('buddy_reaction_pack_custom', 'Customized pack')}</MenuItem>}</Select></FormControl>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.25 }}><Typography variant="body2" sx={{ fontWeight: 700, mr: 0.5 }}>{tr('buddy_speed', 'Speed')}</Typography><IconButton size="small" sx={{ border: '1px solid #C1C7B8' }} disabled={draft.speed <= 0.7} onClick={() => setDraft({ ...draft, speed: Math.max(0.7, Number((draft.speed - 0.1).toFixed(1))) })}>−</IconButton><Typography sx={{ minWidth: 48, textAlign: 'center', fontWeight: 700 }}>{Number(draft.speed).toFixed(1)}×</Typography><IconButton size="small" sx={{ border: '1px solid #C1C7B8' }} disabled={draft.speed >= 1.2} onClick={() => setDraft({ ...draft, speed: Math.min(1.2, Number((draft.speed + 0.1).toFixed(1))) })}>+</IconButton></Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 0.75 }}>{REACTION_KEYS.map((key) => <TextField key={key} size="small" label={tr(`buddy_reaction_${key}`, key[0].toUpperCase() + key.slice(1))} value={draft.reactions[key] || ''} disabled={Boolean(editingBuddy?.isBuiltin)} inputProps={{ maxLength: 16, style: { textAlign: 'center', fontSize: 20, padding: 8 } }} onChange={(event) => setDraft({ ...draft, reactionPackId: 'custom', reactions: { ...draft.reactions, [key]: event.target.value } })} />)}</Box>
      </Box>

      <Box sx={{ p: 1.5, border: '1px solid #C1C7B8', borderRadius: 2.5, bgcolor: '#F5F8F1' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 800 }}>{tr('buddy_image', 'Buddy image')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title={editingBuddy?.isBuiltin ? tr('buddy_leafy_image_protected', 'Leafy’s image is protected') : draft.image ? tr('buddy_change_image', 'Change buddy image') : tr('buddy_choose_image', 'Choose buddy image')}>
            <Box
              component={editingBuddy?.isBuiltin ? 'div' : 'label'}
              sx={{
                position: 'relative',
                width: 76,
                height: 76,
                flex: '0 0 auto',
                borderRadius: '50%',
                cursor: editingBuddy?.isBuiltin ? 'default' : 'pointer',
                display: 'grid',
                placeItems: 'center',
                bgcolor: '#DCE8C8',
                border: '2px solid #AFC48A',
                boxShadow: '0 3px 10px rgba(45, 57, 19, 0.14)',
                transition: 'transform 120ms ease, box-shadow 120ms ease',
                '&:hover': editingBuddy?.isBuiltin ? {} : { transform: 'translateY(-1px)', boxShadow: '0 5px 14px rgba(45, 57, 19, 0.2)' },
                '&:focus-within': { outline: '3px solid rgba(88, 111, 60, 0.25)', outlineOffset: 2 },
              }}
            >
              {draft.image ? (
                <Box component="img" src={draft.image} alt={tr('buddy_preview_alt', 'Buddy preview')} sx={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <Typography aria-hidden sx={{ fontSize: 32, lineHeight: 1 }}>{draft.reactions?.neutral || '🙂'}</Typography>
              )}
              {!editingBuddy?.isBuiltin && <Box sx={{ position: 'absolute', right: -3, bottom: -3, width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#586F3C', color: 'white', border: '2px solid #F5F8F1' }}>
                <PhotoCameraRoundedIcon sx={{ fontSize: 16 }} />
              </Box>}
              {!editingBuddy?.isBuiltin && <input hidden type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={loadImage} />}
            </Box>
          </Tooltip>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{editingBuddy?.isBuiltin ? tr('buddy_leafy_image_protected', 'Leafy’s image is protected') : draft.image ? tr('buddy_image_selected', 'Image selected') : tr('buddy_add_friendly_face', 'Add a friendly face for this buddy')}</Typography>
            <Typography variant="caption" color="text.secondary">{editingBuddy?.isBuiltin ? tr('buddy_restore_hint', 'Use Restore to return Leafy to its original settings.') : tr('buddy_image_formats', 'JPG, PNG, WebP or GIF · up to 750 KB')}</Typography>
          </Box>
          {draft.image && !editingBuddy?.isBuiltin && (
            <Tooltip title={tr('buddy_remove_image', 'Remove buddy image')}>
              <IconButton
                size="small"
                color="error"
                aria-label={tr('buddy_remove_image', 'Remove buddy image')}
                onClick={() => setDraft({ ...draft, image: '' })}
                sx={{ border: '1px solid', borderColor: 'error.light' }}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>{tr('buddy_image_storage_note', 'The buddy image is saved privately and syncs across your devices.')}</Typography>
      </Box>
    </Stack></DialogContent>
    <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #C1C7B8', flexWrap: 'wrap', gap: 0.5 }}><Box sx={{ flex: 1 }} /><Button size="small" onClick={onClose}>{tr('sb_cancel', 'Cancel')}</Button><Button size="small" variant="contained" disabled={saving || !draft.name.trim()} onClick={save}>{saving ? tr('buddy_saving', 'Saving…') : editingId ? tr('buddy_save_select', 'Save & select') : tr('buddy_create_select', 'Create & select')}</Button></DialogActions>
  </Dialog>;
}
